/**
 * Asset-template contract (the slots vocabulary) and the fill-result slot
 * shapes it produces.
 *
 * `FimAssetTemplateData` IS llm-service's `MonitoringTemplate` model
 * (`llm_service/template_fill/models.py`) plus a `template_version` stamp;
 * `FilledSlot` / `RepairItem` mirror the same file's output models. Templates
 * are stored as `fim-asset-template` entries; the fill result is ABSORBED into
 * the `fim-asset-instance` entry (`FimAssetInstanceData.fill` in `types.ts`) —
 * there is no separate fill entry.
 */

export const FIM_ENTRY_ASSET_TEMPLATE = 'fim-asset-template'

// ---------------------------------------------------------------------------
// Template definition (authored by the Templates tab, read by llm-service)
// ---------------------------------------------------------------------------

export interface PiTagSlotDef {
  key: string
  label: string
  measurement_class?: string | null
  qualifier?: string | null
  unit?: string | null
  required?: boolean
  intent?: string | null
  thresholds?: Record<string, unknown> | null
}

export interface DocumentSlotDef {
  key: string
  label: string
  doc_kind?: string | null
  required?: boolean
}

/** The `data` payload of a `fim-asset-template` entry. */
export interface FimAssetTemplateData {
  /** Identity slug, stable across edits (edits are IN PLACE, same entry id). */
  template_id: string
  /** Content hash of the canonical slots JSON — bumped on every real edit so
   * idempotent saves don't churn versions. No immutable version lineage. */
  template_version: string
  name: string
  /** Matched casefolded against a subject's `data.type`; 1:1 per type per site. */
  applies_to_type: string
  pi_tag_slots: PiTagSlotDef[]
  document_slots: DocumentSlotDef[]
}

/** Deterministic entry id: `fim-asset-template-<template_id>`. */
export const assetTemplateEntryId = (templateId: string): string =>
  `${FIM_ENTRY_ASSET_TEMPLATE}-${templateId}`

/** Loose structural check for a template payload read back from Rolodex. */
export const isAssetTemplateData = (
  data: unknown
): data is FimAssetTemplateData =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as { template_id?: unknown }).template_id === 'string' &&
  Array.isArray((data as { pi_tag_slots?: unknown }).pi_tag_slots) &&
  Array.isArray((data as { document_slots?: unknown }).document_slots)

// ---------------------------------------------------------------------------
// Fill-result slots (rendered from `FimAssetInstanceData.fill.slots`)
// ---------------------------------------------------------------------------

export type FilledSlotStatus = 'bound' | 'review' | 'unresolved'

/** A candidate binding awaiting human adjudication (`status: 'review'`). */
export interface FilledSlotCandidate {
  ref?: string | null
  subject_uid?: string | null
  reason?: string | null
}

/** A historian PI-tag alternative considered for a sensor (evidence detail). */
export interface PiTagCandidate {
  tag: string
  score?: number
  band?: 'HIGH' | 'MEDIUM' | 'LOW'
  description?: string
}

/**
 * Why a historian PI tag was chosen for a bound sensor — mirrors llm-service
 * `PiTagEvidence` (`llm_service/template_fill/models.py`). `instrument_id` is
 * the sensor's ISA tag (e.g. 01-TT-FGS163A); `ref` is the historian datapoint
 * it resolved to (e.g. BOP1.01-TT-FGS163A) and may be empty when the sensor
 * isn't historised; `datapoint_id` is the historian datapoint entry id.
 *
 * This list is THE source of bound-ref ↔ instrument pairing for rendering
 * (fill-ownership protocol v2 §9) — the slot-level `bound_refs` and
 * `instrument_ids` lists are deliberately NOT index-aligned.
 */
export interface PiTagEvidence {
  instrument_id?: string
  ref?: string
  datapoint_id?: string
  band?: 'HIGH' | 'MEDIUM' | 'LOW'
  match_reason?: string
  /** pid_confirmed | canonical_match | name_desc_search | mock. */
  basis?: string
  unit?: string
  validated?: boolean
  validation_note?: string
  alternatives?: PiTagCandidate[]
}

export interface FilledSlot {
  key: string
  slot_family: 'pi_tag' | 'document'
  label?: string | null
  measurement_class?: string | null
  doc_kind?: string | null
  required?: boolean
  status: FilledSlotStatus
  /** Historian PI tags (pi_tag slots) or `DOC-…` document ids (document slots). */
  bound_refs: string[]
  /** Instrument (ISA / P&ID sensor) tags for a pi_tag slot — kept even when a
   * sensor has no historian datapoint. Empty for document slots. */
  instrument_ids?: string[]
  /** Human-readable names index-aligned with `bound_refs` (document filenames);
   * empty for pi_tag slots, where the refs are the tags. */
  bound_names?: string[]
  bound_subject_uids: string[]
  channel_count?: number | null
  candidates?: FilledSlotCandidate[]
  confidence?: number | null
  /** How the bind was substantiated: measured | derived | proxied. */
  basis?: string | null
  thresholds?: Record<string, unknown> | null
  why?: string | null
  /** Historian evidence for a pi_tag slot — the ONLY pairing source between
   * bound refs and instruments (each element carries ref/instrument_id/
   * datapoint_id; see `PiTagEvidence`). Empty for document slots. */
  pi_tag_evidence?: PiTagEvidence[]
}

export interface RepairItem {
  ref?: string | null
  ref_type?: string | null
  subject_uid?: string | null
  reason?: string | null
  disposition?: string | null
}
